import { ScreenshareTransition } from '../../features/screenshare/screenshare.types';
import { type ScreenshareEventData } from './screenshareState.types';
import { useScreenshareState } from './useScreenshareState';

type UseScreenshareStateActionsProps = {
  setInitialize: () => Promise<void>;
  setReceivedRequest: (realtimeMemberId: string) => Promise<void>;
  setAcceptRequest: () => Promise<void>;
  setRejectRequest: (eventData?: ScreenshareEventData) => Promise<void>;
  setCancelRequest: (eventData?: ScreenshareEventData) => Promise<void>;
  setSendRequest: (realtimeMemberId: string) => Promise<void>;
  setStopScreenshare: (eventData?: ScreenshareEventData) => Promise<void>;
  setStoppedScreenshare: () => Promise<void>;
};

export const useScreenshareTransitions =
  (): UseScreenshareStateActionsProps => {
    const { transition } = useScreenshareState();

    const setInitialize = async (): Promise<void> => {
      await transition(ScreenshareTransition.INITIALIZE);
    };

    const setReceivedRequest = async (
      realtimeMemberId: string,
    ): Promise<void> => {
      await transition(ScreenshareTransition.RECEIVED_SCREENSHARE_REQUEST, {
        realtimeMemberId,
      });
    };

    const setAcceptRequest = async (): Promise<void> => {
      await transition(ScreenshareTransition.ACCEPT_SCREENSHARE_REQUEST);
    };

    const setCancelRequest = async (
      eventData?: ScreenshareEventData,
    ): Promise<void> => {
      await transition(
        ScreenshareTransition.CANCEL_SCREENSHARE_REQUEST,
        eventData,
      );
    };

    const setRejectRequest = async (
      eventData?: ScreenshareEventData,
    ): Promise<void> => {
      await transition(
        ScreenshareTransition.REJECT_SCREENSHARE_REQUEST,
        eventData,
      );
    };

    const setSendRequest = async (realtimeMemberId: string): Promise<void> => {
      await transition(ScreenshareTransition.SEND_SCREENSHARE_REQUEST, {
        realtimeMemberId,
      });
    };

    const setStopScreenshare = async (
      eventData?: ScreenshareEventData,
    ): Promise<void> => {
      await transition(ScreenshareTransition.STOP_SCREENSHARE, eventData);
    };

    // Special case for when the Patient stops screen share before it has fully started for the Provider.
    const setStoppedScreenshare = async (): Promise<void> => {
      await transition(ScreenshareTransition.STOP_SCREENSHARE);
    };

    return {
      setInitialize,
      setReceivedRequest,
      setAcceptRequest,
      setRejectRequest,
      setCancelRequest,
      setSendRequest,
      setStopScreenshare,
      setStoppedScreenshare,
    };
  };
